import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, FileText, AlertTriangle, Scale, Shield, Users } from 'lucide-react';

const Villkor = () => {
  const { language } = useLanguage();

  const content = {
    sv: {
      title: "Användarvillkor",
      subtitle: "Villkor för användning av Skogskalkyl",
      back: "Tillbaka till startsidan",
      lastUpdated: "Senast uppdaterad: Mars 2026",
      sections: [
        {
          icon: FileText,
          title: "1. Godkännande av villkor",
          content: "Genom att använda Skogskalkyl godkänner du dessa användarvillkor. Om du inte accepterar villkoren ska du inte använda tjänsten.\n\nSkogskalkyl tillhandahålls av Hodema edertools AB."
        },
        {
          icon: AlertTriangle,
          title: "2. Beräkningar är uppskattningar",
          content: "Alla beräkningar som görs i Skogskalkyl är uppskattningar baserade på statistiska genomsnittsvärden.\n\n• Resultaten är indikativa och ska användas som vägledning\n• Faktiska värden kan avvika betydligt från beräknade värden\n• Lokala förhållanden, marknadssituation och andra faktorer påverkar faktiska resultat"
        },
        {
          icon: Scale,
          title: "3. Ingen professionell rådgivning",
          content: "Skogskalkyl ersätter inte professionell rådgivning.\n\n• Beräkningarna utgör inte ekonomisk, juridisk eller skoglig rådgivning\n• Vi rekommenderar att alltid konsultera kvalificerade rådgivare före ekonomiska beslut\n• Skogsmäklarverktyg ersätter inte professionella värderingar"
        },
        {
          icon: Users,
          title: "4. Användarkonto",
          content: "För att använda kalkylatorerna krävs ett användarkonto.\n\n• Du ansvarar för att skydda dina inloggningsuppgifter\n• Du får inte dela ditt konto med andra\n• Vi förbehåller oss rätten att avsluta konton som bryter mot villkoren"
        },
        {
          icon: Shield,
          title: "5. Ansvarsbegränsning",
          content: "Användaren ansvarar själv för beslut baserade på Skogskalkyl.\n\n• Vi garanterar inte att beräkningarna är korrekta eller kompletta\n• Vi ansvarar inte för eventuella förluster eller skador som uppstår till följd av användning av tjänsten\n• Tjänsten tillhandahålls \"i befintligt skick\" utan garantier"
        },
        {
          icon: FileText,
          title: "6. Datakällor",
          content: "Beräkningarna baseras på officiell svensk statistik:\n\n• SLU Riksskogstaxeringen\n• Skogsstyrelsen\n• SCB\n\nVi strävar efter att hålla data aktuell men garanterar inte att all information är uppdaterad."
        },
        {
          icon: FileText,
          title: "7. Ändringar av villkor",
          content: "Vi förbehåller oss rätten att ändra dessa villkor. Väsentliga ändringar meddelas via e-post eller på webbplatsen."
        },
        {
          icon: FileText,
          title: "8. Tillämplig lag",
          content: "Dessa villkor regleras av svensk lag. Tvister ska i första hand lösas genom förhandling, i andra hand genom svensk domstol."
        }
      ]
    },
    de: {
      title: "Nutzungsbedingungen",
      subtitle: "Bedingungen für die Nutzung von Skogskalkyl",
      back: "Zurück zur Startseite",
      lastUpdated: "Letzte Aktualisierung: März 2026",
      sections: [
        {
          icon: FileText,
          title: "1. Annahme der Bedingungen",
          content: "Durch die Nutzung von Skogskalkyl akzeptieren Sie diese Nutzungsbedingungen. Wenn Sie die Bedingungen nicht akzeptieren, nutzen Sie den Dienst nicht.\n\nSkogskalkyl wird von Hodema edertools AB bereitgestellt."
        },
        {
          icon: AlertTriangle,
          title: "2. Berechnungen sind Schätzungen",
          content: "Alle Berechnungen in Skogskalkyl sind Schätzungen basierend auf statistischen Durchschnittswerten.\n\n• Die Ergebnisse sind indikativ und dienen als Orientierung\n• Tatsächliche Werte können erheblich von berechneten Werten abweichen\n• Lokale Bedingungen, Marktsituation und andere Faktoren beeinflussen tatsächliche Ergebnisse"
        },
        {
          icon: Scale,
          title: "3. Keine professionelle Beratung",
          content: "Skogskalkyl ersetzt keine professionelle Beratung.\n\n• Die Berechnungen stellen keine wirtschaftliche, rechtliche oder forstliche Beratung dar\n• Wir empfehlen, vor wirtschaftlichen Entscheidungen qualifizierte Berater zu konsultieren\n• Makler-Tools ersetzen keine professionellen Bewertungen"
        },
        {
          icon: Users,
          title: "4. Benutzerkonto",
          content: "Für die Nutzung der Kalkulatoren ist ein Benutzerkonto erforderlich.\n\n• Sie sind für den Schutz Ihrer Anmeldedaten verantwortlich\n• Sie dürfen Ihr Konto nicht mit anderen teilen\n• Wir behalten uns das Recht vor, Konten zu kündigen, die gegen die Bedingungen verstoßen"
        },
        {
          icon: Shield,
          title: "5. Haftungsbeschränkung",
          content: "Der Benutzer ist selbst für Entscheidungen basierend auf Skogskalkyl verantwortlich.\n\n• Wir garantieren nicht, dass die Berechnungen korrekt oder vollständig sind\n• Wir haften nicht für Verluste oder Schäden, die durch die Nutzung des Dienstes entstehen\n• Der Dienst wird \"wie besehen\" ohne Garantien bereitgestellt"
        },
        {
          icon: FileText,
          title: "6. Datenquellen",
          content: "Die Berechnungen basieren auf offiziellen schwedischen Statistiken:\n\n• SLU Reichswaldinventur\n• Schwedische Forstbehörde\n• SCB\n\nWir bemühen uns, Daten aktuell zu halten, garantieren aber nicht, dass alle Informationen auf dem neuesten Stand sind."
        },
        {
          icon: FileText,
          title: "7. Änderungen der Bedingungen",
          content: "Wir behalten uns das Recht vor, diese Bedingungen zu ändern. Wesentliche Änderungen werden per E-Mail oder auf der Website mitgeteilt."
        },
        {
          icon: FileText,
          title: "8. Anwendbares Recht",
          content: "Diese Bedingungen unterliegen schwedischem Recht. Streitigkeiten sollen zunächst durch Verhandlung, andernfalls durch schwedische Gerichte gelöst werden."
        }
      ]
    },
    en: {
      title: "Terms of Use",
      subtitle: "Terms and conditions for using Skogskalkyl",
      back: "Back to homepage",
      lastUpdated: "Last updated: March 2026",
      sections: [
        {
          icon: FileText,
          title: "1. Acceptance of terms",
          content: "By using Skogskalkyl you accept these terms of use. If you do not accept the terms, do not use the service.\n\nSkogskalkyl is provided by Hodema edertools AB."
        },
        {
          icon: AlertTriangle,
          title: "2. Calculations are estimates",
          content: "All calculations in Skogskalkyl are estimates based on statistical average values.\n\n• Results are indicative and should be used as guidance\n• Actual values may differ significantly from calculated values\n• Local conditions, market situation and other factors affect actual results"
        },
        {
          icon: Scale,
          title: "3. No professional advice",
          content: "Skogskalkyl does not replace professional advice.\n\n• Calculations do not constitute economic, legal or forestry advice\n• We recommend always consulting qualified advisors before economic decisions\n• Broker tools do not replace professional valuations"
        },
        {
          icon: Users,
          title: "4. User account",
          content: "A user account is required to use the calculators.\n\n• You are responsible for protecting your login credentials\n• You may not share your account with others\n• We reserve the right to terminate accounts that violate the terms"
        },
        {
          icon: Shield,
          title: "5. Limitation of liability",
          content: "The user is responsible for decisions based on Skogskalkyl.\n\n• We do not guarantee that calculations are correct or complete\n• We are not liable for any losses or damages arising from use of the service\n• The service is provided \"as is\" without warranties"
        },
        {
          icon: FileText,
          title: "6. Data sources",
          content: "Calculations are based on official Swedish statistics:\n\n• SLU National Forest Inventory\n• Swedish Forest Agency\n• SCB\n\nWe strive to keep data current but do not guarantee that all information is up to date."
        },
        {
          icon: FileText,
          title: "7. Changes to terms",
          content: "We reserve the right to change these terms. Material changes will be notified via email or on the website."
        },
        {
          icon: FileText,
          title: "8. Applicable law",
          content: "These terms are governed by Swedish law. Disputes shall be resolved primarily through negotiation, otherwise through Swedish courts."
        }
      ]
    },
    se: {
      title: "Geavahaneavttut",
      subtitle: "Eavttut Skogskalkyl geavaheapmái",
      back: "Ruoktot álgosiidui",
      lastUpdated: "Maŋimustá ođasmahtton: Njukčamánnu 2026",
      sections: [
        {
          icon: FileText,
          title: "1. Eavttuid dohkkeheapmi",
          content: "Go geavahit Skogskalkyl de dohkkehat dáid geavahaneavttuid.\n\nSkogskalkyl fállojuvvo Hodema edertools AB bokte."
        },
        {
          icon: AlertTriangle,
          title: "2. Rehkenastimat leat árvvoštallamat",
          content: "Buot rehkenastimat Skogskalkyl:as leat árvvoštallamat vuođđuduvvon statistihkalaš gaskamearreárvvuide.\n\n• Bohtosat leat indikatiivvat ja galget geavahuvvot bagadallan\n• Duohta árvvut sáhttet earret mearkkašahtti láhkai"
        },
        {
          icon: Shield,
          title: "3. Ovddasvástádusa ráddjehus",
          content: "Geavaheaddji ieš vástida mearrádusain mat vuođđuduvvojit Skogskalkyl:ii."
        }
      ]
    }
  };

  const t = content[language] || content.sv;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="villkor-page">
      <Link 
        to="/" 
        className="inline-flex items-center text-sm text-forest-600 hover:text-forest-700"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {t.back}
      </Link>

      <div>
        <h1 className="text-3xl font-heading font-bold text-forest-800">{t.title}</h1>
        <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        <p className="text-sm text-muted-foreground mt-1">{t.lastUpdated}</p>
      </div>

      <div className="space-y-6">
        {t.sections.map((section, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <section.icon className="h-5 w-5 text-forest-600" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {section.content}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Villkor;
